import { Card, Group, Select, TextInput } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useSnapshot } from "valtio";
import { assistantLogTexts } from "@/locales/assistant-log";
import { assistantUsageLogTexts } from "@/locales/assistant-usage-log";
import { i18nStore } from "@/store/i18n";
import {
	ASSISTANT_LOG_MODALITIES,
	ASSISTANT_LOG_STATUSES,
	type AssistantLogModality,
	type AssistantLogStatus,
} from "@/types/ai-assistant-log";
import {
	USAGE_LOG_VENDORS,
	type UsageLogVendor,
} from "@/types/ai-assistant-usage-log";
import { logModalityLabel, logStatusLabel } from "./log-format.logic";
import { vendorLabel } from "./usage-log.logic";
import {
	ASSISTANT_LOG_RANGE_OPTIONS,
	type AssistantLogFilters,
} from "./use-assistant-log";

const ALL = "";
const SELECT_WIDTH = 150;

interface Props {
	filters: AssistantLogFilters;
	setFilters: (next: AssistantLogFilters) => void;
	search: string;
	setSearch: (next: string) => void;
	/** Teks placeholder kotak cari; lapis 1 cari pengguna, lapis 2 cari judul. */
	searchPlaceholder?: string;
}

/** Filter bar bersama lapis 1 & 2: rentang, vendor, status, modalitas, kotak cari. */
export function LogFilters({
	filters,
	setFilters,
	search,
	setSearch,
	searchPlaceholder,
}: Props) {
	const { lang: uiLang } = useSnapshot(i18nStore);
	const lang = uiLang === "en" ? "en" : "id";
	const t = assistantLogTexts[lang];
	const vendorText = assistantUsageLogTexts[lang];

	return (
		<Card withBorder radius="md" p="md" data-log-filters>
			<Group gap="sm" align="flex-end">
				<Select
					label={t.rangeLabel}
					size="xs"
					w={SELECT_WIDTH}
					allowDeselect={false}
					data={ASSISTANT_LOG_RANGE_OPTIONS.map((days) => ({
						value: String(days),
						label: t.rangeOption(days),
					}))}
					value={String(filters.days)}
					onChange={(value) =>
						setFilters({ ...filters, days: Number(value ?? filters.days) })
					}
				/>
				<Select
					label={t.vendorLabel}
					size="xs"
					w={SELECT_WIDTH}
					allowDeselect={false}
					data={[
						{ value: ALL, label: t.all },
						...USAGE_LOG_VENDORS.map((vendor) => ({
							value: vendor,
							label: vendorLabel(vendor, vendorText),
						})),
					]}
					value={filters.vendor ?? ALL}
					onChange={(value) =>
						setFilters({
							...filters,
							vendor: value ? (value as UsageLogVendor) : undefined,
						})
					}
				/>
				<Select
					label={t.statusLabel}
					size="xs"
					w={SELECT_WIDTH}
					allowDeselect={false}
					data={[
						{ value: ALL, label: t.all },
						...ASSISTANT_LOG_STATUSES.map((status) => ({
							value: status,
							label: logStatusLabel(status, t),
						})),
					]}
					value={filters.status ?? ALL}
					onChange={(value) =>
						setFilters({
							...filters,
							status: value ? (value as AssistantLogStatus) : undefined,
						})
					}
				/>
				<Select
					label={t.modalityLabel}
					size="xs"
					w={SELECT_WIDTH}
					allowDeselect={false}
					data={[
						{ value: ALL, label: t.all },
						...ASSISTANT_LOG_MODALITIES.map((modality) => ({
							value: modality,
							label: logModalityLabel(modality, t),
						})),
					]}
					value={filters.modality ?? ALL}
					onChange={(value) =>
						setFilters({
							...filters,
							modality: value ? (value as AssistantLogModality) : undefined,
						})
					}
				/>
				<TextInput
					size="xs"
					flex={1}
					miw={220}
					leftSection={<IconSearch size={14} />}
					placeholder={searchPlaceholder ?? t.searchPlaceholder}
					value={search}
					onChange={(event) => setSearch(event.currentTarget.value)}
					aria-label={searchPlaceholder ?? t.searchPlaceholder}
				/>
			</Group>
		</Card>
	);
}
